import React from "react";
import {
  Close,
  MoreVert,
  Send,
  LockOpen,
  Visibility,
  Preview,
} from "@mui/icons-material";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Divider,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
} from "@mui/material";
import {
  Campaign,
  CampaignState as CampaignStateType,
  CampaignType,
} from "../../types";
import { Link } from "react-router-dom";
import { CampaignState } from "./State";
import { CampaignProgress } from "./Progress";
import { ReleaseResultsConfirmModal } from "./ReleaseResultsConfirmModal";
import { useTranslation } from "react-i18next";
import { findCampaignActivity } from "../../Data";

export interface Props {
  campaign: Campaign;
  onChange: (campaign: Campaign, activityLabel: string) => void;
  onDelete: (campaign: Campaign) => void;
  onRemind: (campaign: Campaign) => void;
}

export const CampaignCard = ({
  campaign,
  onChange,
  onDelete,
  onRemind,
}: Props) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [showReleaseConfirm, setShowReleaseConfirm] = React.useState(false);
  const [t] = useTranslation();
  const open = Boolean(anchorEl);

  const isActive =
    campaign.state === CampaignStateType.Active ||
    campaign.state === CampaignStateType.AssignmentActive;
  const isClosable =
    campaign.state === CampaignStateType.Active ||
    campaign.state === CampaignStateType.AssignmentActive;
  const isClosed =
    campaign.state === CampaignStateType.Closed ||
    campaign.state === CampaignStateType.AssignmentClosed;
  const isDeleteable =
    campaign.state === CampaignStateType.Draft ||
    campaign.state === CampaignStateType.AssignmentScheduled;
  const isScheduled =
    campaign.state === CampaignStateType.Scheduled ||
    campaign.state === CampaignStateType.AssignmentScheduled;
  const isDraftWithSendDate =
    campaign.state === CampaignStateType.Draft && campaign.autoSendDate;
  const isDraft = campaign.state === CampaignStateType.Draft;
  const tPrefix =
    campaign.campaignType === CampaignType.Assignment
      ? "assignments"
      : "surveys";

  //
  // Event Handlers
  //

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  function handleScheduleClick() {
    onChange(
      { ...campaign, state: CampaignStateType.Scheduled },
      t(`${tPrefix}.events.schedule`),
    );
  }

  function handleCloseClick() {
    onChange(
      { ...campaign, state: CampaignStateType.Closed },
      t(`${tPrefix}.events.close`),
    );
  }

  function handleDeleteClick() {
    onDelete(campaign);
  }

  function handleReopenClick() {
    onChange(
      { ...campaign, state: CampaignStateType.Active },
      t(`${tPrefix}.events.reopen`),
    );
  }

  function handleReleaseClick() {
    setShowReleaseConfirm(true);
  }

  function doRelease() {
    onChange(
      { ...campaign, released: new Date().toISOString() },
      t(`${tPrefix}.events.reopen`),
    );
  }

  function handleUnreleaseClick() {
    onChange(
      { ...campaign, released: null },
      t(`${tPrefix}.menu.unreleaseResults`),
    );
  }

  function handleRemindClick() {
    onRemind(campaign);
  }

  function handleCancelClick() {
    onChange(
      {
        ...campaign,
        state: CampaignStateType.Draft,
        autoSendDate: null,
      },
      t(`${tPrefix}.events.cancelSchedule`),
    );
  }

  //
  // Helpers
  //

  const sentActivity = findCampaignActivity(
    campaign,
    t(`${tPrefix}.events.send`),
  );

  return (
    <Card>
      {showReleaseConfirm && (
        <ReleaseResultsConfirmModal
          onCancel={() => setShowReleaseConfirm(false)}
          onConfirm={() => {
            setShowReleaseConfirm(false);
            doRelease();
          }}
        />
      )}
      <CardHeader
        action={
          <>
            <CampaignState sx={{ ml: 2 }} campaign={campaign} />
            <IconButton
              aria-label="more"
              aria-controls={open ? "campaign-menu" : undefined}
              aria-haspopup="true"
              aria-expanded={open ? "true" : undefined}
              onClick={handleClick}
            >
              <MoreVert />
            </IconButton>
          </>
        }
        title={campaign.name}
        titleTypographyProps={{
          component: Link,
          to: `/campaigns/${campaign.id}`,
          style: { color: "inherit", textDecoration: "none" },
        }}
        subheader={
          <>
            {campaign.form ? <>{campaign.form.name}</> : <>No template</>}
            <br />
            {sentActivity
              ? new Date(sentActivity.created).toLocaleString()
              : ""}
          </>
        }
      />
      <Menu
        id="campaign-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: "visible",
            filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
            mt: 1.5,
            "& .MuiAvatar-root": {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1,
            },
            "&:before": {
              content: '""',
              display: "block",
              position: "absolute",
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: "background.paper",
              transform: "translateY(-50%) rotate(45deg)",
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <MenuItem component={Link} to={`/campaigns/${campaign.id}`}>
          <ListItemIcon>
            <Visibility fontSize="small" />
          </ListItemIcon>
          {t(`${tPrefix}.menu.viewSurvey`)}
        </MenuItem>

        <Divider />

        {isScheduled && (
          <MenuItem onClick={handleCancelClick}>
            <ListItemIcon>
              <Close fontSize="small" />
            </ListItemIcon>
            {t(`${tPrefix}.menu.cancelSchedule`)}
          </MenuItem>
        )}

        {isClosed && (
          <MenuItem onClick={handleReopenClick}>
            <ListItemIcon>
              <LockOpen fontSize="small" />
            </ListItemIcon>
            {t(`${tPrefix}.menu.reopen`)}
          </MenuItem>
        )}

        {isClosed &&
          (campaign.released === null ? (
            <MenuItem onClick={handleReleaseClick}>
              <ListItemIcon>
                <Preview fontSize="small" />
              </ListItemIcon>
              {t(`${tPrefix}.menu.releaseResults`)}
            </MenuItem>
          ) : (
            <MenuItem onClick={handleUnreleaseClick}>
              <ListItemIcon>
                <Preview fontSize="small" />
              </ListItemIcon>
              {t(`${tPrefix}.menu.resultsReleased`)}
            </MenuItem>
          ))}

        {isDraftWithSendDate && (
          <MenuItem key="schedule" onClick={handleScheduleClick}>
            <ListItemIcon>
              <Send fontSize="small" />
            </ListItemIcon>
            {t(`${tPrefix}.menu.schedule`)}
          </MenuItem>
        )}

        {isActive && (
          <MenuItem key="reminder" onClick={handleRemindClick}>
            <ListItemIcon>
              <Send fontSize="small" />
            </ListItemIcon>
            {t(`${tPrefix}.menu.sendReminder`)}
          </MenuItem>
        )}

        {isClosable && (
          <MenuItem key="close" onClick={handleCloseClick}>
            <ListItemIcon>
              <Close fontSize="small" />
            </ListItemIcon>
            {t(`${tPrefix}.menu.close`)}
          </MenuItem>
        )}

        {isDeleteable && (
          <MenuItem key="delete" onClick={handleDeleteClick}>
            <ListItemIcon>
              <Close fontSize="small" />
            </ListItemIcon>
            {t(`${tPrefix}.menu.delete`)}
          </MenuItem>
        )}
      </Menu>

      {!isDraft && (
        <CardContent>
          <Box
            sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}
          >
            <CampaignProgress
              submitted={campaign.submittedResponses}
              total={campaign.totalResponses}
            />
          </Box>
        </CardContent>
      )}
    </Card>
  );
};
